import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import ErrorBoundary from "@/components/error-boundary";
import { useSearchParameters } from "@/components/signup/hooks";
import { ufoEventRedirectToProduct } from "@/components/signup/utils";
import { trackPerceivedProvisioningEvent } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics";
import {
    experiments,
    ExperimentView,
    getExperimentWithExposures
} from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import {
    useCloudStatus,
    useCloudStatusRedirection,
    useCreateSampleProject,
    useTenantAwareStatsigClientUpdater
} from "../../hooks";
import { JiraAnimatedProvisioningScreen } from "../jira-animated-provisioning-screen/jira-animated-provisioning-screen";

import { ANALYTICS_PREFIX } from "./common/constants";
import { useEligibleForSampleProject } from "./controllers";
import { useIsWizardVariation } from "./hooks/use-is-wizard-variation";
import { useCreateRedirectUrlWithWizard } from "./utils/create-redirect-url";
import { Jfind1663CreateProjectTaskPollingProvider } from "./wizard/variant/jfind-1663-page-invite/hooks/use-jfind-1663-create-project-task-polling-context";
import { WelcomeModal } from "./wizard/welcome-modal";

export const JiraProvisioningWithOnboardingWizard = () => {
    // HOOKS
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const {
        createCloudStatus,
        startPerceivedProvisioningTimer,
        perceivedProvisioningStartTime
    } = useCloudStatus();
    const {
        setWaitingForProvisioning,
        addBlockingRedirectPromise,
        resolveBlockingRedirectPromises,
        createRedirectUrl,
        redirectUrl
    } = useCloudStatusRedirection();
    const { products, signupId } = useSearchParameters();
    const { createSampleProject } = useCreateSampleProject();
    const { userEligibleForSampleProject } = useEligibleForSampleProject();
    const { updatedStatsigClientWithCloudId } =
        useTenantAwareStatsigClientUpdater();
    const { isWizardVariation, withSampleData } = useIsWizardVariation({
        updatedStatsigClientWithCloudId
    });
    const createRedirectUrlWithWizard = useCreateRedirectUrlWithWizard();

    // STATE
    const productKey = products.length > 0 ? products[0] : undefined;
    const provisioningEventFired = useRef(false);
    const [siteProvisionedOnMount] = useState(createCloudStatus === "success");
    const createSampleProjectRequestInitialized = useRef(false);

    const isSampleProjectVariation = useMemo(() => {
        if (!userEligibleForSampleProject || !updatedStatsigClientWithCloudId) {
            return false;
        }

        const sampleProjectExperiment = getExperimentWithExposures(
            experiments.jfindMatureExampleProjectExperiment
        );

        return (
            sampleProjectExperiment.type === "enrolled" &&
            sampleProjectExperiment.value
        );
    }, [userEligibleForSampleProject, updatedStatsigClientWithCloudId]);

    useEffect(() => {
        if (
            isSampleProjectVariation &&
            createCloudStatus === "success" &&
            createSampleProjectRequestInitialized.current === false
        ) {
            createSampleProjectRequestInitialized.current = true;
            const sampleProjectPromise = createSampleProject();
            addBlockingRedirectPromise(sampleProjectPromise);
        }
    }, [
        isSampleProjectVariation,
        userEligibleForSampleProject,
        createCloudStatus,
        addBlockingRedirectPromise,
        createSampleProject
    ]);

    useEffect(() => {
        setWaitingForProvisioning(true);
    }, [setWaitingForProvisioning]);

    useEffect(() => {
        startPerceivedProvisioningTimer();
    }, [startPerceivedProvisioningTimer]);

    const trackProvisioningEvent = () => {
        if (!provisioningEventFired.current) {
            trackPerceivedProvisioningEvent({
                createAnalyticsEvent,
                productKey,
                finalProvisioningScreen: "jira-onboarding-wizard",
                startTimeSeconds: perceivedProvisioningStartTime,
                signupId
            });
            provisioningEventFired.current = true;
        }
    };

    const onComplete = async (taskId?: string, onRedirect?: () => void) => {
        const productUrl = checkFeatureFlagGate(
            featureFlags.jiraOnboardingMultipleProjectRequestsBugfix
        )
            ? createRedirectUrl()
            : redirectUrl;

        if (productUrl) {
            await resolveBlockingRedirectPromises();

            if (onRedirect) {
                try {
                    onRedirect();
                } catch (error) {
                    // NO-OP, prevent redirect from failing
                }
            }

            createAnalyticsEvent({
                eventName: "redirectedToProduct",
                eventComponent: "confirmationPage",
                event: "redirected",
                eventType: "ui",
                action: "redirected",
                attributes: {
                    reactComponent: "JiraProvisioningWithOnboardingWizard"
                }
            }).fire();

            // SLO "good event" for redirect into product
            ufoEventRedirectToProduct.success({
                customData: {
                    analyticsEvent: "redirected",
                    redirectFrom: "confirmationPage",
                    reactComponent: "JiraProvisioningWithOnboardingWizard",
                    finalProvisioningScreen: "jira-onboarding-wizard"
                }
            });

            window.location.assign(
                createRedirectUrlWithWizard(productUrl, taskId)
            );
        }
    };

    const handleOnError = useCallback(
        (error: Error) => {
            createAnalyticsEvent({
                eventType: "operational",
                actionSubject: `${ANALYTICS_PREFIX}Screen`,
                action: "failed",
                attributes: {
                    error: error.message
                }
            }).fire();
        },
        [createAnalyticsEvent]
    );

    // This acts like ProductRedirector to avoid showing JiraAnimatedProvisioningScreen if site is ready
    if (siteProvisionedOnMount && !isWizardVariation) {
        return null;
    }

    if (isWizardVariation) {
        trackProvisioningEvent();
    }

    // Handles both cases where site is still provisioning and we show wizard
    // or show wizard immediately when site provisioned on mount
    if (isWizardVariation) {
        return (
            <ErrorBoundary
                fallback={<JiraAnimatedProvisioningScreen />}
                onError={handleOnError}
            >
                <ExperimentView
                    experiment={experiments.jfindOnboardingWizardInviteStep}
                    render={(value) =>
                        value ? (
                            <Jfind1663CreateProjectTaskPollingProvider>
                                <WelcomeModal
                                    // @ts-ignore
                                    withSampleData={withSampleData}
                                    onComplete={onComplete}
                                />
                            </Jfind1663CreateProjectTaskPollingProvider>
                        ) : (
                            <WelcomeModal
                                // @ts-ignore
                                withSampleData={withSampleData}
                                onComplete={onComplete}
                            />
                        )
                    }
                    indeterminate={
                        <WelcomeModal
                            // @ts-ignore
                            withSampleData={withSampleData}
                            onComplete={onComplete}
                        />
                    }
                />
            </ErrorBoundary>
        );
    }

    return <JiraAnimatedProvisioningScreen />;
};
